import type {
  ContactOrderAppointmentRow,
  ContactOrderDigitalChannel,
  ContactOrderDigitalPlan,
  RowCommunicationSelection,
} from "../contact-orders-contract"
import type { ContactOrderPostalSelection } from "../../../../../api/kehrblatt/contact-orders"
import {
  digitalChannelLimitDisabledReason,
  filterDigitalPlanForContactOrderRow,
  reconcilePostalSelectionForContactOrderRow,
} from "../channels"

const hasOwn = (input: object, key: string) => Object.prototype.hasOwnProperty.call(input, key)

export type ContactOrderSelectionState = {
  communicationSelectionByEventId: Record<string, RowCommunicationSelection>
}

export const createContactOrderSelectionState = (): ContactOrderSelectionState => ({
  communicationSelectionByEventId: {},
})

const sameValue = (left: unknown, right: unknown) => JSON.stringify(left) === JSON.stringify(right)

const filterSelectionForRow = (
  row: ContactOrderAppointmentRow,
  selection: RowCommunicationSelection,
  allowedContactChannels: readonly string[],
): RowCommunicationSelection => ({
  digitalByCustomerId: filterDigitalPlanForContactOrderRow(
    row,
    selection.digitalByCustomerId,
    allowedContactChannels,
  ),
  postal: reconcilePostalSelectionForContactOrderRow(row, selection.postal, allowedContactChannels),
})

export const automaticCommunicationSelectionForPlanningRow = (
  row: ContactOrderAppointmentRow,
  allowedContactChannels: readonly string[],
): RowCommunicationSelection => {
  const digitalByCustomerId: ContactOrderDigitalPlan = {}
  for (const tag of row.planningPreview?.digital_channel_tags ?? []) {
    if (!tag.planned) continue

    digitalByCustomerId[tag.customer_id] = [
      ...(digitalByCustomerId[tag.customer_id] ?? []),
      tag.channel,
    ]
  }

  return filterSelectionForRow(
    row,
    {
      digitalByCustomerId,
      postal: row.planningPreview?.postal_selection ?? { mode: "DISABLED" },
    },
    allowedContactChannels,
  )
}

export const hasExplicitCommunicationSelection = (
  row: ContactOrderAppointmentRow,
  state: ContactOrderSelectionState,
) => hasOwn(state.communicationSelectionByEventId, String(row.id))

export const communicationSelectionForPlanningRow = (
  row: ContactOrderAppointmentRow,
  state: ContactOrderSelectionState,
  allowedContactChannels: readonly string[],
): RowCommunicationSelection => {
  const rowKey = String(row.id)

  return hasOwn(state.communicationSelectionByEventId, rowKey)
    ? filterSelectionForRow(
        row,
        state.communicationSelectionByEventId[rowKey],
        allowedContactChannels,
      )
    : automaticCommunicationSelectionForPlanningRow(row, allowedContactChannels)
}

export const selectedCustomerIdsForPlanningRow = (
  row: ContactOrderAppointmentRow,
  state: ContactOrderSelectionState,
  allowedContactChannels: readonly string[],
) => {
  const selection = communicationSelectionForPlanningRow(row, state, allowedContactChannels)
  const selected = new Set(
    Object.entries(selection.digitalByCustomerId)
      .filter(([, channels]) => channels.length)
      .map(([customerId]) => customerId),
  )

  if (selection.postal.mode === "PERSONAL" && selection.postal.customer_id)
    selected.add(selection.postal.customer_id)

  return row.recipients
    .map((recipient) => recipient.customerId)
    .filter((customerId) => selected.has(customerId))
}

export const selectedDigitalChannelsForPlanningRow = (
  row: ContactOrderAppointmentRow,
  state: ContactOrderSelectionState,
  allowedContactChannels: readonly string[],
) => {
  const selected = new Set<ContactOrderDigitalChannel>()
  Object.values(
    communicationSelectionForPlanningRow(row, state, allowedContactChannels).digitalByCustomerId,
  ).forEach((channels) => channels.forEach((channel) => selected.add(channel)))

  return ["EMAIL", "MOBILE_MESSAGE", "PHONE"].filter((channel) =>
    selected.has(channel as ContactOrderDigitalChannel),
  ) as ContactOrderDigitalChannel[]
}

export const hasActiveCommunicationForPlanningRow = (
  row: ContactOrderAppointmentRow,
  state: ContactOrderSelectionState,
  allowedContactChannels: readonly string[],
) => {
  const selection = communicationSelectionForPlanningRow(row, state, allowedContactChannels)

  return (
    Object.values(selection.digitalByCustomerId).some((channels) => channels.length > 0) ||
    selection.postal.mode !== "DISABLED"
  )
}

const withSelection = (
  state: ContactOrderSelectionState,
  rowKey: string,
  selection: RowCommunicationSelection,
): ContactOrderSelectionState => ({
  communicationSelectionByEventId: {
    ...state.communicationSelectionByEventId,
    [rowKey]: selection,
  },
})

export const toggleDigitalChannelSelectionState = ({
  row,
  customerId,
  channel,
  state,
  allowedContactChannels,
}: {
  row: ContactOrderAppointmentRow
  customerId: string
  channel: ContactOrderDigitalChannel
  state: ContactOrderSelectionState
  allowedContactChannels: readonly string[]
}): ContactOrderSelectionState => {
  const current = communicationSelectionForPlanningRow(row, state, allowedContactChannels)
  const currentChannels = current.digitalByCustomerId[customerId] ?? []
  const adding = !currentChannels.includes(channel)

  if (
    adding &&
    digitalChannelLimitDisabledReason({
      digitalPlan: current.digitalByCustomerId,
      customerId,
      channel,
    })
  ) {
    return state
  }

  const changed = { ...current.digitalByCustomerId }
  const nextChannels = adding
    ? [...currentChannels, channel]
    : currentChannels.filter((entry) => entry !== channel)

  if (nextChannels.length) changed[customerId] = nextChannels
  else delete changed[customerId]

  const nextDigital = filterDigitalPlanForContactOrderRow(row, changed, allowedContactChannels)

  if (adding && !nextDigital[customerId]?.includes(channel)) return state

  return withSelection(state, String(row.id), {
    digitalByCustomerId: nextDigital,
    postal: current.postal,
  })
}

export const setPostalSelectionState = ({
  row,
  postal,
  state,
  allowedContactChannels,
}: {
  row: ContactOrderAppointmentRow
  postal: ContactOrderPostalSelection
  state: ContactOrderSelectionState
  allowedContactChannels: readonly string[]
}): ContactOrderSelectionState => {
  const normalized = reconcilePostalSelectionForContactOrderRow(row, postal, allowedContactChannels)

  if (!sameValue(normalized, postal)) return state

  const current = communicationSelectionForPlanningRow(row, state, allowedContactChannels)

  if (hasExplicitCommunicationSelection(row, state) && sameValue(current.postal, normalized)) {
    return state
  }

  return withSelection(state, String(row.id), {
    digitalByCustomerId: current.digitalByCustomerId,
    postal: normalized,
  })
}

export const resetPlanSelectionState = (
  row: ContactOrderAppointmentRow,
  state: ContactOrderSelectionState,
): ContactOrderSelectionState => {
  const rowKey = String(row.id)

  if (!hasOwn(state.communicationSelectionByEventId, rowKey)) return state

  const selected = { ...state.communicationSelectionByEventId }
  delete selected[rowKey]

  return { communicationSelectionByEventId: selected }
}

export const reconcileSelectionStateForRows = (
  rows: ContactOrderAppointmentRow[],
  state: ContactOrderSelectionState,
  allowedContactChannels: readonly string[],
): ContactOrderSelectionState => {
  const rowById = new Map(rows.map((row) => [String(row.id), row]))
  const selected: Record<string, RowCommunicationSelection> = {}
  for (const [rowKey, selection] of Object.entries(state.communicationSelectionByEventId)) {
    const row = rowById.get(rowKey)

    if (row) selected[rowKey] = filterSelectionForRow(row, selection, allowedContactChannels)
  }

  return sameValue(state.communicationSelectionByEventId, selected)
    ? state
    : { communicationSelectionByEventId: selected }
}
